import * as Sentry from '@sentry/vue';

import type { AppLogContext } from '#ui-shared/utils/logger/contexts/log-contexts';
import { MapLoggingContext, UserLoggingContext, WebViewLoggingContext } from '#ui-shared/utils/logger/contexts/log-contexts';
import type { LoggerAdapter } from '#ui-shared/utils/logger/LoggerClass';

/**
 * This is an adapter for logging to Sentry. It implements the LoggerAdapter interface and provides methods for logging messages at different levels (log, error, warn, info, debug) to Sentry.
 */
export class SentryAdapter implements LoggerAdapter {
  log(message: string, context?: AppLogContext): void {
    if (context) {
      Sentry.withScope((scope) => {
        SentryAdapter.handleContext(scope, context);
        Sentry.logger.info(message, context);
      });
    }
    else {
      Sentry.logger.info(message);
    }
  }

  error(message: string, context?: AppLogContext): void {
    if (context) {
      Sentry.withScope((scope) => {
        SentryAdapter.handleContext(scope, context);
        Sentry.logger.error(message, context);
      });
    }
    else {
      Sentry.logger.error(message);
    }
  }

  warn(message: string, context?: AppLogContext): void {
    if (context) {
      Sentry.withScope((scope) => {
        SentryAdapter.handleContext(scope, context);
        Sentry.logger.warn(message, context);
      });
    }
    else {
      Sentry.logger.warn(message);
    }
  }

  info(message: string, context?: AppLogContext): void {
    if (context) {
      Sentry.withScope((scope) => {
        SentryAdapter.handleContext(scope, context);
        Sentry.logger.info(message, context);
      });
    }
    else {
      Sentry.logger.info(message);
    }
  }

  debug(message: string, context?: AppLogContext): void {
    if (context) {
      Sentry.withScope((scope) => {
        SentryAdapter.handleContext(scope, context);
        Sentry.logger.debug(message, context);
      });
    }
    else {
      Sentry.logger.debug(message);
    }
  }

  /**
   * Inspects the provided logging context and sets appropriate tags and user information in the Sentry scope based on the type of context data.
   * @param scope
   * @param context
   */
  static handleContext(scope: Sentry.Scope, context: AppLogContext): void {
    for (const [key, contextData] of Object.entries(context)) {
      if (contextData instanceof UserLoggingContext) {
        scope.setTag(`domain`, `user`);
        scope.setUser(SentryAdapter.toSentryUserContext(contextData));
      }
      else if (contextData instanceof MapLoggingContext) {
        scope.setTag('domain', `map`);
        scope.setContext(key, contextData);
      }
      else if (contextData instanceof WebViewLoggingContext) {
        scope.setTag('domain', `webview`);
        scope.setContext(key, contextData);
      }
      // Catch-all for plain objects like { mapId: '123', isReady: true }
      else if (contextData !== undefined) {
        scope.setContext(
          key,
          (contextData !== null && typeof contextData === 'object' ? contextData : { value: contextData }) as Record<string, unknown>,
        );
      }
    }
  }

  /**
   * @returns a boolean indicating whether Sentry logging is enabled based on the environment variable `VITE_FRONTEND_SENTRY_ENABLED`. If the variable is not set, it defaults to false. This method is used to determine whether to initialize Sentry and log messages to it based on the application's configuration.
   */
  static isSentryEnabled(): boolean {
    const env = import.meta.env.VITE_FRONTEND_SENTRY_ENABLED;

    if (!env) {
      return false;
    }

    return JSON.parse(env);
  }

  /**
   * @returns the release version for Sentry from the environment variables. If no version is provided, it returns 'unknown'. This method is used to initialize Sentry with the correct release version for tracking errors and events in different versions of the application.
   */
  static getReleaseVersionForSentry(): string {
    const env = import.meta.env.VITE_APP_VERSION;

    if (!env) {
      return 'unknown';
    }
    return env;
  }

  /**
   * This method is used to initialize Sentry with the correct DSN for logging errors and events.
   *
   * @returns the Sentry DSN from the environment variables, or `undefined`.
   */
  static getSentryDSN(): string | undefined {
    const env = import.meta.env.VITE_FRONTEND_SENTRY_DSN;

    if (!env) {
      console.error('No Sentry DSN provided. Please let your system administrator know. Sentry will now be disabled.');
      return undefined;
    }
    return env;
  }

  /**
   * @returns a boolean indicating whether to send default personally identifiable information (PII) to Sentry based on the environment variable `VITE_FRONTEND_SENSITIVE_ENV`. If the variable is not set, it defaults to false. See Sentry documentation for more details: https://docs.sentry.io/platforms/javascript/configuration/options/#senddefaultpii.
   */
  static shouldSendDefaultPiiToSentry(): boolean {
    const env = import.meta.env.VITE_FRONTEND_SENSITIVE_ENV;

    if (!env) {
      return false;
    }

    return !JSON.parse(env);
  }

  static generateEnvironmentStringForSentry(): string {
    if (window.location.hostname === 'localhost') {
      return 'localhost';
    }
    else {
      // This will split on the first subdomain so 'dev', 'stg', and 'peb'.
      return window.location.hostname.split('.')[0] ?? '';
    }
  }

  static toSentryUserContext(userContext: UserLoggingContext): Sentry.User {
    return {
      id: userContext.userId,
      username: userContext.username,
    };
  }
}
